import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, Users, UserPlus, Crown, Shield, User, Loader2, 
  Mail, Trash2, Check, AlertCircle, LogOut, Settings,
  ChevronDown, ChevronUp, Copy, CheckCircle, Info, Activity, Calendar,
  Image as ImageIcon, UserCheck, UserMinus, FileText, Filter,
  Instagram, Twitter, Linkedin, Facebook, Share2, Eye, CheckCircle2, Cloud, Clock,
  Heart, MessageCircle, Repeat, BarChart3, PieChart as PieChartIcon, TrendingUp,
  Link2, MousePointerClick, ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { calendarsApi } from '../services/calendarsApi';
import { 
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend 
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Chart colors for platforms
const CHART_COLORS = {
  instagram: '#E1306C',
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  facebook: '#1877F2',
  tiktok: '#000000',
  bluesky: '#0085FF',
  unknown: '#64748b'
};

// Legend formatter for Recharts - extracted to avoid unstable nested component
const legendFormatter = (value) => <span className="text-slate-300 text-xs">{value}</span>;

const TeamSettings = ({ onClose }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDetails, setTeamDetails] = useState(null);
  const [teamLimits, setTeamLimits] = useState(null);
  const [teamActivity, setTeamActivity] = useState([]);
  const [activeTab, setActiveTab] = useState('members'); // 'members' | 'activity' | 'content'
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [teamCalendars, setTeamCalendars] = useState([]);
  const [memberFilter, setMemberFilter] = useState('all');
  const [selectedPost, setSelectedPost] = useState(null); // For viewing post details
  const [userEngagement, setUserEngagement] = useState({ liked: false, commented: false, reposted: false });
  const [engagementSummary, setEngagementSummary] = useState(null);
  const [savingEngagement, setSavingEngagement] = useState(false);
  
  // Create team state
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  // Invite member state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  const fetchTeamActivity = async (teamId) => {
    if (!user?.uid || !teamId) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/teams/${teamId}/activity?user_id=${user.uid}&limit=50`);
      if (response.ok) {
        const data = await response.json();
        setTeamActivity(data.activities || []);
      }
    } catch (err) {
      console.error('Failed to fetch team activity:', err);
    }
  };

  const fetchTeamCalendars = async (teamId) => {
    if (!user?.uid || !teamId) return;
    try {
      // Use the dedicated team calendars endpoint to get all team member calendars
      const data = await calendarsApi.getTeamCalendars(teamId, user.uid, 100);
      setTeamCalendars(data.calendars || []);
    } catch (err) {
      console.error('Failed to fetch team calendars:', err);
    }
  };

  // Engagement tracking functions
  const fetchUserEngagement = async (postId) => {
    if (!user?.uid || !postId) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/engagements/${postId}/${user.uid}`);
      if (response.ok) {
        const data = await response.json();
        setUserEngagement({
          liked: data.liked || false,
          commented: data.commented || false,
          reposted: data.reposted || false
        });
      }
    } catch (err) {
      console.error('Failed to fetch engagement:', err);
    }
  };

  const fetchEngagementSummary = async (postId) => {
    if (!postId) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/engagements/${postId}/summary?team_id=${selectedTeam || ''}`);
      if (response.ok) {
        const data = await response.json();
        setEngagementSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch engagement summary:', err);
    }
  };

  const saveEngagement = async (postId, engagement) => {
    if (!user?.uid || !postId) return;
    setSavingEngagement(true);
    try {
      await fetch(`${BACKEND_URL}/api/engagements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post_id: postId,
          user_id: user.uid,
          user_name: user.displayName || user.email,
          team_id: selectedTeam,
          ...engagement
        })
      });
      setUserEngagement(engagement);
      // Refresh summary after saving
      fetchEngagementSummary(postId);
    } catch (err) {
      console.error('Failed to save engagement:', err);
    } finally {
      setSavingEngagement(false);
    }
  };

  // Load engagement data when a post is selected
  const handleSelectPost = (post) => {
    setSelectedPost(post);
    setUserEngagement({ liked: false, commented: false, reposted: false });
    setEngagementSummary(null);
    if (post.id) {
      fetchUserEngagement(post.id);
      fetchEngagementSummary(post.id);
    }
  };

  const fetchTeamLimits = async () => {
    if (!user?.uid) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/teams/limits/${user.uid}?user_email=${encodeURIComponent(user.email || '')}`);
      if (response.ok) {
        const data = await response.json();
        setTeamLimits(data);
      }
    } catch (err) {
      console.error('Failed to fetch team limits:', err);
    }
  };

  const fetchUserTeams = async () => {
    if (!user?.uid) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const [teamsResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/teams/user/${user.uid}`),
        fetchTeamLimits()
      ]);
      
      if (!teamsResponse.ok) throw new Error('Failed to fetch teams');
      
      const data = await teamsResponse.json();
      setTeams(data.teams || []);
      
      // Auto-select first team if available
      if (data.teams && data.teams.length > 0 && !selectedTeam) {
        setSelectedTeam(data.teams[0].team.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTeamDetails = async (teamId) => {
    if (!user?.uid || !teamId) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/teams/${teamId}?user_id=${user.uid}`);
      if (!response.ok) throw new Error('Failed to fetch team details');
      
      const data = await response.json();
      setTeamDetails(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchUserTeams();
  }, [user]);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamDetails(selectedTeam);
      fetchTeamActivity(selectedTeam);
      fetchTeamCalendars(selectedTeam);
    }
  }, [selectedTeam]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim() || !user?.uid) return;
    
    setIsCreating(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newTeamName.trim(),
          owner_id: user.uid,
          owner_email: user.email,
          owner_name: user.displayName || user.email
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to create team');
      
      setSuccess('Team created successfully!');
      setShowCreateTeam(false);
      setNewTeamName('');
      fetchUserTeams();
      setSelectedTeam(data.team.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !selectedTeam || !user?.uid) return;
    
    setIsInviting(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/teams/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          team_id: selectedTeam,
          email: inviteEmail.trim().toLowerCase(),
          invited_by: user.uid,
          invited_by_name: user.displayName || user.email,
          origin_url: window.location.origin
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to send invitation (${response.status})`);
      }
      
      const data = await response.json();
      setSuccess(`Invitation sent to ${inviteEmail}`);
      setShowInvite(false);
      setInviteEmail('');
      fetchTeamDetails(selectedTeam);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    console.log('handleRemoveMember called', memberId);
    if (!selectedTeam || !user?.uid) {
      console.log('Missing selectedTeam or user.uid');
      return;
    }
    
    setError(null);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/teams/${selectedTeam}/members/${memberId}?requester_id=${user.uid}`,
        { method: 'DELETE' }
      );
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to remove member');
      
      setSuccess('Member removed successfully');
      fetchTeamDetails(selectedTeam);
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.message);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    if (!selectedTeam || !user?.uid) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/teams/${selectedTeam}/members/${memberId}/role?requester_id=${user.uid}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: newRole })
        }
      );
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update role');
      
      setSuccess('Role updated successfully');
      fetchTeamDetails(selectedTeam);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLeaveTeam = async () => {
    if (!selectedTeam || !user?.uid) return;
    if (!window.confirm('Are you sure you want to leave this team?')) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/teams/${selectedTeam}/leave?user_id=${user.uid}`,
        { method: 'POST' }
      );
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to leave team');
      
      setSuccess('You have left the team');
      setSelectedTeam(null);
      setTeamDetails(null);
      fetchUserTeams();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam || !user?.uid) return;
    if (!window.confirm('Are you sure you want to delete this team? This cannot be undone.')) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/teams/${selectedTeam}?user_id=${user.uid}`,
        { method: 'DELETE' }
      );
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to delete team');
      
      setSuccess('Team deleted successfully');
      setSelectedTeam(null);
      setTeamDetails(null);
      fetchUserTeams();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancelInvite = async (inviteId, email) => {
    console.log('handleCancelInvite called', { inviteId, email, selectedTeam, userId: user?.uid });
    
    if (!selectedTeam || !user?.uid) {
      console.log('Missing selectedTeam or user.uid');
      return;
    }
    
    setError(null);
    
    try {
      const url = `${BACKEND_URL}/api/teams/invite/${inviteId}?user_id=${user.uid}`;
      console.log('Calling DELETE:', url);
      
      const response = await fetch(url, { method: 'DELETE' });
      
      const data = await response.json();
      console.log('Response:', response.status, data);
      
      if (!response.ok) throw new Error(data.detail || 'Failed to cancel invitation');
      
      setSuccess(`Invitation to ${email} cancelled`);
      fetchTeamDetails(selectedTeam);
    } catch (err) {
      console.error('Error cancelling invite:', err);
      setError(err.message);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-amber-400" />;
      case 'admin': return <Shield className="w-4 h-4 text-indigo-400" />;
      default: return <User className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case 'calendar_generated': return <Calendar className="w-4 h-4 text-indigo-400" />;
      case 'profile_created': return <UserPlus className="w-4 h-4 text-green-400" />;
      case 'profile_updated': return <Settings className="w-4 h-4 text-blue-400" />;
      case 'member_joined': return <UserCheck className="w-4 h-4 text-emerald-400" />;
      case 'member_left': return <UserMinus className="w-4 h-4 text-orange-400" />;
      default: return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };

  const formatActivityMessage = (activity) => {
    const name = activity.user_name || activity.user_email;
    switch (activity.action) {
      case 'calendar_generated':
        return `${name} generated a ${activity.details?.days || 7}-day calendar for "${activity.details?.profile_name}"`;
      case 'profile_created':
        return `${name} created profile "${activity.details?.profile_name}"`;
      case 'profile_updated':
        return `${name} updated profile "${activity.details?.profile_name}"`;
      case 'member_joined':
        return `${name} joined the team`;
      case 'member_left':
        return `${name} left the team`;
      default:
        return `${name} performed an action`;
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const currentTeamData = teams.find(t => t.team.id === selectedTeam);
  const isOwner = teamDetails?.user_role === 'owner';
  const isAdmin = teamDetails?.user_role === 'admin' || isOwner;

  // Platform icons and colors
  const PLATFORM_ICONS = {
    instagram: Instagram,
    twitter: Twitter,
    linkedin: Linkedin,
    facebook: Facebook,
    tiktok: Share2,
    bluesky: Cloud,
  };

  const PLATFORM_COLORS = {
    instagram: 'text-pink-400',
    twitter: 'text-blue-400',
    linkedin: 'text-blue-600',
    facebook: 'text-blue-500',
    tiktok: 'text-slate-300',
    bluesky: 'text-sky-400',
  };

  // Calculate posting stats for a calendar
  const getPostingStats = (calendar) => {
    const posts = calendar.posts || [];
    const total = posts.length;
    const posted = posts.filter(p => p.status === 'posted').length;
    return { total, posted, percentage: total > 0 ? Math.round((posted / total) * 100) : 0 };
  };

  // Team content stats
  const teamContentStats = useMemo(() => {
    let totalPosts = 0;
    let totalPosted = 0;
    const memberStats = {};
    const platformStats = {};

    teamCalendars.forEach(cal => {
      const stats = getPostingStats(cal);
      totalPosts += stats.total;
      totalPosted += stats.posted;

      // Member stats
      const memberId = cal.user_id;
      if (!memberStats[memberId]) {
        memberStats[memberId] = { name: cal.user_name || cal.user_email, total: 0, posted: 0 };
      }
      memberStats[memberId].total += stats.total;
      memberStats[memberId].posted += stats.posted;

      // Platform stats
      (cal.posts || []).forEach(post => {
        const platform = post.platform || 'unknown';
        if (!platformStats[platform]) {
          platformStats[platform] = { total: 0, posted: 0 };
        }
        platformStats[platform].total += 1;
        if (post.status === 'posted') {
          platformStats[platform].posted += 1;
        }
      });
    });

    return { totalPosts, totalPosted, memberStats, platformStats, calendarsCount: teamCalendars.length };
  }, [teamCalendars]);

  // Get flat list of all published posts across all team members
  const allPublishedPosts = useMemo(() => {
    const posts = [];
    teamCalendars.forEach(cal => {
      (cal.posts || []).forEach(post => {
        if (post.status === 'posted') {
          posts.push({
            ...post,
            user_name: cal.user_name || cal.user_email,
            user_id: cal.user_id,
            calendar_name: cal.profile_name,
          });
        }
      });
    });
    // Sort by posted_at date (most recent first)
    return posts.sort((a, b) => {
      const dateA = a.posted_at ? new Date(a.posted_at) : new Date(0);
      const dateB = b.posted_at ? new Date(b.posted_at) : new Date(0);
      return dateB - dateA;
    });
  }, [teamCalendars]);

  // Filter published posts by member
  const filteredPublishedPosts = useMemo(() => {
    if (memberFilter === 'all') return allPublishedPosts;
    return allPublishedPosts.filter(post => post.user_id === memberFilter);
  }, [allPublishedPosts, memberFilter]);

  // Chart data for platforms
  const platformChartData = useMemo(() => {
    if (!teamContentStats?.platformStats) return [];
    return Object.entries(teamContentStats.platformStats)
      .filter(([_, stats]) => stats?.posted > 0)
      .map(([platform, stats]) => ({
        name: platform.charAt(0).toUpperCase() + platform.slice(1),
        value: stats.posted || 0,
        total: stats.total || 0,
        color: CHART_COLORS[platform.toLowerCase()] || CHART_COLORS.unknown
      }));
  }, [teamContentStats?.platformStats]);

  // Chart data for team members
  const memberChartData = useMemo(() => {
    if (!teamContentStats?.memberStats) return [];
    return Object.entries(teamContentStats.memberStats)
      .map(([id, stats]) => ({
        name: stats?.name?.split('@')[0] || 'Unknown',
        posted: stats?.posted || 0,
        pending: (stats?.total || 0) - (stats?.posted || 0),
        total: stats?.total || 0
      }))
      .sort((a, b) => b.posted - a.posted);
  }, [teamContentStats?.memberStats]);

  // Team engagement stats (aggregated)
  const [teamEngagementStats, setTeamEngagementStats] = useState({ liked: 0, commented: 0, reposted: 0, loading: true });
  
  // URL analytics state
  const [urlAnalytics, setUrlAnalytics] = useState({ total_urls: 0, total_clicks: 0, top_urls: [], loading: true });
  
  useEffect(() => {
    let isCancelled = false;
    
    const fetchTeamEngagement = async () => {
      if (!selectedTeam || allPublishedPosts.length === 0) {
        setTeamEngagementStats({ liked: 0, commented: 0, reposted: 0, loading: false });
        return;
      }
      
      setTeamEngagementStats(prev => ({ ...prev, loading: true }));
      
      let totalLiked = 0, totalCommented = 0, totalReposted = 0;
      
      // Fetch engagement summary for each post (batch this in production)
      const postsToFetch = allPublishedPosts.slice(0, 20); // Limit to 20 most recent
      
      try {
        for (const post of postsToFetch) {
          if (isCancelled) return;
          
          try {
            const response = await fetch(`${BACKEND_URL}/api/engagements/${post.id}/summary?team_id=${selectedTeam}`);
            if (response.ok) {
              const summary = await response.json();
              totalLiked += summary.liked_count || 0;
              totalCommented += summary.commented_count || 0;
              totalReposted += summary.reposted_count || 0;
            }
          } catch (err) {
            console.error('Error fetching engagement:', err);
          }
        }
        
        if (!isCancelled) {
          setTeamEngagementStats({ liked: totalLiked, commented: totalCommented, reposted: totalReposted, loading: false });
        }
      } catch (err) {
        if (!isCancelled) {
          setTeamEngagementStats({ liked: 0, commented: 0, reposted: 0, loading: false });
        }
      }
    };
    
    if (activeTab === 'content' && selectedTeam) {
      fetchTeamEngagement();
    }
    
    return () => {
      isCancelled = true;
    };
  }, [activeTab, selectedTeam, allPublishedPosts.length]); // Only depend on length, not the whole array

  // Fetch URL analytics
  useEffect(() => {
    const fetchUrlAnalytics = async () => {
      if (!selectedTeam) {
        setUrlAnalytics({ total_urls: 0, total_clicks: 0, top_urls: [], loading: false });
        return;
      }
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/url-analytics/team/${selectedTeam}`);
        if (response.ok) {
          const data = await response.json();
          setUrlAnalytics({ 
            total_urls: data.total_urls || 0, 
            total_clicks: data.total_clicks || 0, 
            top_urls: data.top_urls || [], 
            loading: false 
          });
        } else {
          setUrlAnalytics({ total_urls: 0, total_clicks: 0, top_urls: [], loading: false });
        }
      } catch (err) {
        console.error('Error fetching URL analytics:', err);
        setUrlAnalytics({ total_urls: 0, total_clicks: 0, top_urls: [], loading: false });
      }
    };
    
    if (activeTab === 'content' && selectedTeam) {
      fetchUrlAnalytics();
    }
  }, [activeTab, selectedTeam]);

  const engagementChartData = useMemo(() => [
    { name: 'Liked', value: teamEngagementStats.liked, color: '#EF4444' },
    { name: 'Commented', value: teamEngagementStats.commented, color: '#3B82F6' },
    { name: 'Reposted', value: teamEngagementStats.reposted, color: '#10B981' }
  ].filter(d => d.value > 0), [teamEngagementStats]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600/20 rounded-lg">
              <Users className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Team Settings</h2>
              <p className="text-sm text-slate-400">Manage your team and members</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Alerts */}
          {error && (
            <div className="mb-4 p-4 bg-red-900/30 border border-red-800/50 rounded-lg flex items-center gap-3 text-red-400">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <p>{error}</p>
              <button onClick={() => setError(null)} className="ml-auto">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-4 bg-green-900/30 border border-green-800/50 rounded-lg flex items-center gap-3 text-green-400">
              <CheckCircle className="w-5 h-5 shrink-0" />
              <p>{success}</p>
              <button onClick={() => setSuccess(null)} className="ml-auto">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Team Limits Info */}
              {teamLimits && (
                <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-4 h-4 text-indigo-400" />
                    <span className="text-sm font-medium text-slate-300">Your Team Limits</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Teams Owned</p>
                      <p className="text-white font-medium">
                        {teamLimits.current.teams_owned} / {teamLimits.limits.max_teams_owned}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Teams Joined</p>
                      <p className="text-white font-medium">
                        {teamLimits.current.teams_joined} / {teamLimits.limits.max_teams_joined}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Members/Team</p>
                      <p className="text-white font-medium">
                        Up to {teamLimits.limits.max_members_per_team}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Team Selector or Create Team */}
              {teams.length === 0 ? (
                <div className="text-center py-10">
                  <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Teams Yet</h3>
                  <p className="text-slate-400 mb-6">Create a team to collaborate with others</p>
                  
                  {showCreateTeam ? (
                    <form onSubmit={handleCreateTeam} className="max-w-sm mx-auto space-y-4">
                      <input
                        type="text"
                        value={newTeamName}
                        onChange={(e) => setNewTeamName(e.target.value)}
                        placeholder="Team name"
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-indigo-500 outline-none"
                        required
                      />
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setShowCreateTeam(false)}
                          className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isCreating || (teamLimits && !teamLimits.can_create_team)}
                          className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                        </button>
                      </div>
                    </form>
                  ) : teamLimits && !teamLimits.can_create_team ? (
                    <div className="text-amber-400 text-sm bg-amber-900/20 border border-amber-800/50 rounded-lg p-4 max-w-sm mx-auto">
                      <p>You have reached your team limit. Upgrade to Pro or Agency to create more teams.</p>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowCreateTeam(true)}
                      className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl flex items-center gap-2 mx-auto"
                    >
                      <UserPlus className="w-5 h-5" />
                      Create Team
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {/* Team Selector */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <select
                        value={selectedTeam || ''}
                        onChange={(e) => setSelectedTeam(e.target.value)}
                        className="bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-indigo-500 outline-none"
                      >
                        {teams.map(({ team, role }) => (
                          <option key={team.id} value={team.id}>
                            {team.name} ({role})
                          </option>
                        ))}
                      </select>
                      
                      {!showCreateTeam && teamLimits?.can_create_team && (
                        <button
                          onClick={() => setShowCreateTeam(true)}
                          className="p-2 text-indigo-400 hover:text-indigo-300 hover:bg-indigo-900/30 rounded-lg transition-colors"
                          title="Create new team"
                        >
                          <UserPlus className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                    
                    {teamDetails && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-400">
                          {teamDetails.members?.length || 0} / {currentTeamData?.team?.max_members || 5} members
                        </span>
                        <span className="px-2 py-0.5 bg-slate-700 rounded text-slate-300 capitalize">
                          {currentTeamData?.team?.plan_type} plan
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Create Team Form (inline) */}
                  {showCreateTeam && (
                    <form onSubmit={handleCreateTeam} className="bg-slate-900/50 border border-slate-700 rounded-xl p-4">
                      <h4 className="text-sm font-bold text-white mb-3">Create New Team</h4>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={newTeamName}
                          onChange={(e) => setNewTeamName(e.target.value)}
                          placeholder="Team name"
                          className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-indigo-500 outline-none"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowCreateTeam(false)}
                          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isCreating}
                          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                        >
                          {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Tabs */}
                  {teamDetails && (
                    <div className="flex gap-2 border-b border-slate-700 pb-2">
                      <button
                        onClick={() => setActiveTab('members')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                          activeTab === 'members'
                            ? 'bg-indigo-600 text-white'
                            : 'text-slate-400 hover:text-white hover:bg-slate-700'
                        }`}
                      >
                        <Users className="w-4 h-4" />
                        Members
                      </button>
                      <button
                        onClick={() => setActiveTab('content')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                          activeTab === 'content'
                            ? 'bg-indigo-600 text-white'
                            : 'text-slate-400 hover:text-white hover:bg-slate-700'
                        }`}
                      >
                        <FileText className="w-4 h-4" />
                        Content
                        {teamContentStats.totalPosted > 0 && (
                          <span className="px-1.5 py-0.5 bg-green-600/30 text-green-400 rounded text-xs">
                            {teamContentStats.totalPosted}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => setActiveTab('activity')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                          activeTab === 'activity'
                            ? 'bg-indigo-600 text-white'
                            : 'text-slate-400 hover:text-white hover:bg-slate-700'
                        }`}
                      >
                        <Activity className="w-4 h-4" />
                        Activity
                        {teamActivity.length > 0 && (
                          <span className="px-1.5 py-0.5 bg-slate-700 rounded text-xs">
                            {teamActivity.length}
                          </span>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Team Members Tab */}
                  {teamDetails && activeTab === 'members' && (
                    <div className="bg-slate-900/50 border border-slate-700 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between p-4 border-b border-slate-700">
                        <h3 className="font-bold text-white">Team Members</h3>
                        {isAdmin && (
                          <button
                            onClick={() => setShowInvite(true)}
                            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-lg flex items-center gap-2"
                          >
                            <UserPlus className="w-4 h-4" />
                            Invite
                          </button>
                        )}
                      </div>
                      
                      {/* Invite Form */}
                      {showInvite && (
                        <form onSubmit={handleInviteMember} className="p-4 bg-slate-800/50 border-b border-slate-700">
                          <div className="flex gap-3">
                            <input
                              type="email"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              placeholder="Email address"
                              className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-indigo-500 outline-none"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowInvite(false)}
                              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={isInviting}
                              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                            >
                              {isInviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                              Send
                            </button>
                          </div>
                        </form>
                      )}
                      
                      {/* Members List */}
                      <div className="divide-y divide-slate-700">
                        {teamDetails.members?.map((member) => (
                          <div key={member.id} className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3">
                              {getRoleIcon(member.role)}
                              <div>
                                <p className="text-white font-medium">
                                  {member.name || member.email}
                                  {member.user_id === user?.uid && (
                                    <span className="ml-2 text-xs text-slate-500">(You)</span>
                                  )}
                                </p>
                                <p className="text-sm text-slate-400">{member.email}</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {isOwner && member.role !== 'owner' && (
                                <select
                                  value={member.role}
                                  onChange={(e) => handleUpdateRole(member.user_id, e.target.value)}
                                  className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                                >
                                  <option value="member">Member</option>
                                  <option value="admin">Admin</option>
                                </select>
                              )}
                              
                              {isAdmin && member.role !== 'owner' && member.user_id !== user?.uid && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemoveMember(member.user_id);
                                  }}
                                  className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                                  title="Remove member"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Pending Invitations */}
                      {teamDetails.pending_invites?.length > 0 && (
                        <>
                          <div className="p-4 border-t border-slate-700 bg-slate-800/30">
                            <h4 className="text-sm font-bold text-slate-400 mb-3">Pending Invitations</h4>
                            <div className="space-y-2">
                              {teamDetails.pending_invites.map((invite) => (
                                <div key={invite.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-amber-400" />
                                    <div>
                                      <span className="text-sm text-slate-300">{invite.email}</span>
                                      <p className="text-xs text-slate-500">
                                        Expires {new Date(invite.expires_at).toLocaleDateString()}
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleCancelInvite(invite.id, invite.email);
                                    }}
                                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg transition-colors"
                                    title="Cancel invitation"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Content Tab - Published Posts Feed */}
                  {teamDetails && activeTab === 'content' && (
                    <div className="space-y-4">
                      {/* Quick Stats */}
                      <div className="flex items-center gap-4 p-4 bg-slate-900/50 border border-slate-700 rounded-xl">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                          <span className="text-lg font-bold text-green-400">{teamContentStats?.totalPosted || 0}</span>
                          <span className="text-sm text-slate-400">posts published</span>
                        </div>
                        <div className="h-6 w-px bg-slate-700" />
                        <div className="flex items-center gap-4">
                          {teamContentStats?.platformStats && Object.entries(teamContentStats.platformStats).map(([platform, stats]) => {
                            if (!stats || stats.posted === 0) return null;
                            const Icon = PLATFORM_ICONS[platform.toLowerCase()] || Share2;
                            const color = PLATFORM_COLORS[platform.toLowerCase()] || 'text-slate-400';
                            return (
                              <div key={platform} className="flex items-center gap-1">
                                <Icon className={`w-4 h-4 ${color}`} />
                                <span className="text-sm text-slate-300">{stats.posted}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Analytics Charts Section */}
                      {teamContentStats.totalPosted > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Posts by Platform Chart */}
                          <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-4">
                            <h4 className="font-semibold text-white flex items-center gap-2 mb-4">
                              <PieChartIcon className="w-4 h-4 text-indigo-400" />
                              Posts by Platform
                            </h4>
                            {platformChartData.length > 0 ? (
                              <div className="h-48">
                                <ResponsiveContainer width="100%" height={192}>
                                  <PieChart>
                                    <Pie
                                      data={platformChartData}
                                      cx="50%"
                                      cy="50%"
                                      innerRadius={40}
                                      outerRadius={70}
                                      paddingAngle={2}
                                      dataKey="value"
                                    >
                                      {platformChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                      ))}
                                    </Pie>
                                    <Tooltip 
                                      contentStyle={{ 
                                        backgroundColor: '#1e293b', 
                                        border: '1px solid #334155',
                                        borderRadius: '8px',
                                        color: '#fff'
                                      }}
                                      formatter={(value, name) => [`${value} posts`, name]}
                                    />
                                    <Legend 
                                      verticalAlign="bottom"
                                      formatter={legendFormatter}
                                    />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <div className="h-48 flex items-center justify-center text-slate-500">
                                No data yet
                              </div>
                            )}
                          </div>

                          {/* Team Engagement Chart */}
                          <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-4">
                            <h4 className="font-semibold text-white flex items-center gap-2 mb-4">
                              <TrendingUp className="w-4 h-4 text-green-400" />
                              Team Engagement
                            </h4>
                            {teamEngagementStats.loading ? (
                              <div className="h-48 flex items-center justify-center">
                                <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                              </div>
                            ) : engagementChartData.length > 0 ? (
                              <div className="h-48">
                                <ResponsiveContainer width="100%" height={192}>
                                  <BarChart data={engagementChartData} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                                    <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                    <YAxis 
                                      type="category" 
                                      dataKey="name" 
                                      tick={{ fill: '#94a3b8', fontSize: 12 }} 
                                      width={80}
                                    />
                                    <Tooltip 
                                      contentStyle={{ 
                                        backgroundColor: '#1e293b', 
                                        border: '1px solid #334155',
                                        borderRadius: '8px',
                                        color: '#fff'
                                      }}
                                    />
                                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                      {engagementChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <div className="h-48 flex flex-col items-center justify-center text-slate-500">
                                <Heart className="w-8 h-8 mb-2 opacity-50" />
                                <p className="text-sm">No engagement tracked yet</p>
                                <p className="text-xs text-slate-600 mt-1">Team members can track their engagement on posts</p>
                              </div>
                            )}
                          </div>

                          {/* Team Member Contribution Chart */}
                          {memberChartData.length > 1 && (
                            <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-4 md:col-span-2">
                              <h4 className="font-semibold text-white flex items-center gap-2 mb-4">
                                <BarChart3 className="w-4 h-4 text-purple-400" />
                                Team Member Contributions
                              </h4>
                              <div className="h-48">
                                <ResponsiveContainer width="100%" height={192}>
                                  <BarChart data={memberChartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                    <XAxis 
                                      dataKey="name" 
                                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                                      interval={0}
                                      angle={-20}
                                      textAnchor="end"
                                      height={50}
                                    />
                                    <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                    <Tooltip 
                                      contentStyle={{ 
                                        backgroundColor: '#1e293b', 
                                        border: '1px solid #334155',
                                        borderRadius: '8px',
                                        color: '#fff'
                                      }}
                                    />
                                    <Legend 
                                      formatter={legendFormatter}
                                    />
                                    <Bar dataKey="posted" name="Posted" fill="#10B981" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="pending" name="Pending" fill="#64748b" radius={[4, 4, 0, 0]} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* URL Analytics Section */}
                      <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-4">
                        <h4 className="font-semibold text-white flex items-center gap-2 mb-4">
                          <Link2 className="w-4 h-4 text-cyan-400" />
                          Short Link Analytics
                          {!urlAnalytics.loading && urlAnalytics.total_clicks > 0 && (
                            <span className="ml-auto text-sm font-normal text-cyan-400">
                              {urlAnalytics.total_clicks} total clicks
                            </span>
                          )}
                        </h4>
                        
                        {urlAnalytics.loading ? (
                          <div className="flex items-center justify-center py-6">
                            <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                          </div>
                        ) : urlAnalytics.top_urls && urlAnalytics.top_urls.length > 0 ? (
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {urlAnalytics.top_urls.map((url, idx) => (
                              <div 
                                key={idx} 
                                className="flex items-center justify-between gap-3 p-2 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-slate-600 transition-colors"
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-slate-300 truncate font-medium">
                                    {url.title || 'Untitled'}
                                  </p>
                                  <p className="text-xs text-slate-500 truncate">
                                    {url.short_url}
                                  </p>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                  <div className="flex items-center gap-1 text-cyan-400">
                                    <MousePointerClick className="w-4 h-4" />
                                    <span className="text-sm font-bold">{url.clicks}</span>
                                  </div>
                                  <a 
                                    href={url.original_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="p-1 text-slate-500 hover:text-white transition-colors"
                                    title="Open original"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-6 text-slate-500">
                            <Link2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No shortened links yet</p>
                            <p className="text-xs text-slate-600 mt-1">Links will appear when team members add sources to posts</p>
                          </div>
                        )}
                      </div>

                      {/* Filter by Member */}
                      {Object.keys(teamContentStats.memberStats).length > 1 && (
                        <div className="flex items-center gap-2">
                          <Filter className="w-4 h-4 text-slate-500" />
                          <select
                            value={memberFilter}
                            onChange={(e) => setMemberFilter(e.target.value)}
                            className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white"
                          >
                            <option value="all">All Members</option>
                            {teamContentStats?.memberStats && Object.entries(teamContentStats.memberStats).map(([id, stats]) => (
                              <option key={id} value={id}>
                                {stats?.name || 'Unknown'} ({stats?.posted || 0} posted)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Published Posts List */}
                      <div className="bg-slate-900/50 border border-slate-700 rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-slate-700">
                          <h3 className="font-bold text-white flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-green-400" />
                            Published Posts ({filteredPublishedPosts.length})
                          </h3>
                        </div>
                        
                        {filteredPublishedPosts.length === 0 ? (
                          <div className="p-8 text-center">
                            <CheckCircle2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                            <p className="text-slate-400">No published posts yet</p>
                            <p className="text-sm text-slate-500 mt-1">
                              Posts will appear here when team members mark them as Posted
                            </p>
                          </div>
                        ) : (
                          <div className="divide-y divide-slate-700 max-h-[400px] overflow-y-auto">
                            {filteredPublishedPosts.map((post, idx) => {
                              const PlatformIcon = PLATFORM_ICONS[(post.platform || '').toLowerCase()] || Share2;
                              const platformColor = PLATFORM_COLORS[(post.platform || '').toLowerCase()] || 'text-slate-400';
                              const hasPostUrl = !!post.post_url;
                              
                              // Get platform URL fallback
                              const getPlatformFallbackUrl = () => {
                                const platform = (post.platform || '').toLowerCase();
                                switch(platform) {
                                  case 'twitter': return 'https://twitter.com';
                                  case 'instagram': return 'https://instagram.com';
                                  case 'linkedin': return 'https://linkedin.com/feed';
                                  case 'facebook': return 'https://facebook.com';
                                  case 'tiktok': return 'https://tiktok.com';
                                  case 'bluesky': return 'https://bsky.app';
                                  default: return null;
                                }
                              };
                              
                              return (
                                <div 
                                  key={idx} 
                                  className="p-4 hover:bg-slate-800/50 cursor-pointer transition-colors border-b border-slate-700/50 last:border-b-0"
                                  onClick={() => {
                                    const url = post.post_url || getPlatformFallbackUrl();
                                    if (url) {
                                      window.open(url, '_blank', 'noopener,noreferrer');
                                    }
                                  }}
                                >
                                  {/* Post Header */}
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-3">
                                      <div className={`p-2 rounded-lg bg-slate-800 ${platformColor}`}>
                                        <PlatformIcon className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <span className="text-sm font-medium text-white capitalize">
                                          {post.platform || 'Unknown'}
                                        </span>
                                        <div className="flex items-center gap-2 text-xs text-slate-500">
                                          <User className="w-3 h-3" />
                                          <span>{post.user_name}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      {post.posted_at && (
                                        <div className="flex items-center gap-1 text-xs text-green-400 bg-green-900/20 px-2 py-1 rounded">
                                          <Clock className="w-3 h-3" />
                                          {new Date(post.posted_at).toLocaleDateString()} {new Date(post.posted_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                        </div>
                                      )}
                                      {/* Mark My Engagement button - MORE VISIBLE */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSelectPost(post);
                                        }}
                                        className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium transition-colors flex items-center gap-1.5"
                                        title="Mark my engagement on this post"
                                      >
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        My Engagement
                                      </button>
                                      {hasPostUrl ? (
                                        <Eye className="w-4 h-4 text-green-400" title="Has direct link" />
                                      ) : (
                                        <Eye className="w-4 h-4 text-slate-500" title="Opens platform" />
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Post Content Preview */}
                                  <p className="text-sm text-slate-300 whitespace-pre-wrap line-clamp-2">
                                    {post.content || post.text || 'No content'}
                                  </p>
                                  
                                  {/* Hashtags */}
                                  {Array.isArray(post.hashtags) && post.hashtags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {post.hashtags.slice(0, 3).map((tag, i) => (
                                        <span key={i} className="text-xs text-indigo-400">
                                          #{String(tag).replace('#', '')}
                                        </span>
                                      ))}
                                      {post.hashtags.length > 3 && (
                                        <span className="text-xs text-slate-500">+{post.hashtags.length - 3} more</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Post Detail Modal */}
                  {selectedPost && (
                    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setSelectedPost(null)}>
                      <div 
                        className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl"
                        onClick={e => e.stopPropagation()}
                      >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-4 border-b border-slate-700">
                          <div className="flex items-center gap-3">
                            {(() => {
                              const PlatformIcon = PLATFORM_ICONS[(selectedPost.platform || '').toLowerCase()] || Share2;
                              const platformColor = PLATFORM_COLORS[(selectedPost.platform || '').toLowerCase()] || 'text-slate-400';
                              return (
                                <div className={`p-2 rounded-lg bg-slate-900 ${platformColor}`}>
                                  <PlatformIcon className="w-5 h-5" />
                                </div>
                              );
                            })()}
                            <div>
                              <h3 className="font-bold text-white capitalize">{selectedPost.platform || 'Post'}</h3>
                              <p className="text-xs text-slate-400">Posted by {selectedPost.user_name}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedPost(null)}
                            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        
                        {/* Modal Content */}
                        <div className="p-4 overflow-y-auto max-h-[60vh]">
                          {/* Posted timestamp */}
                          {selectedPost.posted_at && (
                            <div className="flex items-center gap-2 text-sm text-green-400 mb-4 bg-green-900/20 p-2 rounded-lg">
                              <CheckCircle2 className="w-4 h-4" />
                              Posted on {new Date(selectedPost.posted_at).toLocaleDateString()} at {new Date(selectedPost.posted_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                          )}
                          
                          {/* Full Post Content */}
                          <div className="bg-slate-900 rounded-lg p-4 mb-4">
                            <p className="text-slate-200 whitespace-pre-wrap">
                              {selectedPost.content || selectedPost.text || 'No content'}
                            </p>
                          </div>
                          
                          {/* Hashtags */}
                          {Array.isArray(selectedPost.hashtags) && selectedPost.hashtags.length > 0 && (
                            <div className="mb-4">
                              <p className="text-xs text-slate-500 mb-2">Hashtags</p>
                              <div className="flex flex-wrap gap-2">
                                {selectedPost.hashtags.map((tag, i) => (
                                  <span key={i} className="px-2 py-1 bg-indigo-900/30 text-indigo-400 text-sm rounded">
                                    #{String(tag).replace('#', '')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Post Image if exists */}
                          {selectedPost.imageUrl && (
                            <div className="mb-4">
                              <p className="text-xs text-slate-500 mb-2">Image</p>
                              <img 
                                src={selectedPost.imageUrl} 
                                alt="Post" 
                                className="rounded-lg max-h-48 object-cover"
                              />
                            </div>
                          )}

                          {/* My Engagement Tracking - PROMINENT CHECKBOXES */}
                          <div className="bg-indigo-900/30 border border-indigo-500/50 rounded-lg p-4 mb-4">
                            <h4 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-indigo-400" /> 
                              Mark Your Engagement
                            </h4>
                            <p className="text-xs text-indigo-300/70 mb-4">Check off what you did on this post (only visible to you)</p>
                            
                            <div className="space-y-3">
                              {/* Liked Checkbox */}
                              <label 
                                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                  userEngagement.liked 
                                    ? 'bg-pink-600/20 border border-pink-500/50' 
                                    : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
                                }`}
                                onClick={() => {
                                  const newEngagement = { ...userEngagement, liked: !userEngagement.liked };
                                  saveEngagement(selectedPost.id, newEngagement);
                                }}
                              >
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                  userEngagement.liked 
                                    ? 'bg-pink-500 border-pink-500' 
                                    : 'border-slate-500'
                                }`}>
                                  {userEngagement.liked && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <Heart className={`w-5 h-5 ${userEngagement.liked ? 'text-pink-400 fill-current' : 'text-slate-400'}`} />
                                <span className={`font-medium ${userEngagement.liked ? 'text-pink-300' : 'text-slate-300'}`}>
                                  I liked this post
                                </span>
                              </label>

                              {/* Commented Checkbox */}
                              <label 
                                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                  userEngagement.commented 
                                    ? 'bg-blue-600/20 border border-blue-500/50' 
                                    : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
                                }`}
                                onClick={() => {
                                  const newEngagement = { ...userEngagement, commented: !userEngagement.commented };
                                  saveEngagement(selectedPost.id, newEngagement);
                                }}
                              >
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                  userEngagement.commented 
                                    ? 'bg-blue-500 border-blue-500' 
                                    : 'border-slate-500'
                                }`}>
                                  {userEngagement.commented && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <MessageCircle className={`w-5 h-5 ${userEngagement.commented ? 'text-blue-400 fill-current' : 'text-slate-400'}`} />
                                <span className={`font-medium ${userEngagement.commented ? 'text-blue-300' : 'text-slate-300'}`}>
                                  I commented on this post
                                </span>
                              </label>

                              {/* Reposted/Shared Checkbox */}
                              <label 
                                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                  userEngagement.reposted 
                                    ? 'bg-green-600/20 border border-green-500/50' 
                                    : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
                                }`}
                                onClick={() => {
                                  const newEngagement = { ...userEngagement, reposted: !userEngagement.reposted };
                                  saveEngagement(selectedPost.id, newEngagement);
                                }}
                              >
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                  userEngagement.reposted 
                                    ? 'bg-green-500 border-green-500' 
                                    : 'border-slate-500'
                                }`}>
                                  {userEngagement.reposted && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <Repeat className={`w-5 h-5 ${userEngagement.reposted ? 'text-green-400' : 'text-slate-400'}`} />
                                <span className={`font-medium ${userEngagement.reposted ? 'text-green-300' : 'text-slate-300'}`}>
                                  I reposted/shared this post
                                </span>
                              </label>
                            </div>
                            
                            {savingEngagement && (
                              <p className="text-xs text-indigo-400 mt-2 flex items-center gap-1">
                                <Loader2 className="w-3 h-3 animate-spin" /> Saving...
                              </p>
                            )}
                          </div>

                          {/* Team Engagement Summary (for admins/owners) */}
                          {engagementSummary && (teamDetails?.user_role === 'owner' || teamDetails?.user_role === 'admin') && (
                            <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-lg p-4">
                              <p className="text-xs text-indigo-400 mb-3 flex items-center gap-2">
                                <Users className="w-3 h-3" /> Team Engagement Summary
                              </p>
                              <div className="flex flex-wrap gap-4 text-sm">
                                <div className="flex items-center gap-1">
                                  <Heart className="w-4 h-4 text-pink-400" />
                                  <span className="text-white font-medium">{engagementSummary.liked_count}</span>
                                  <span className="text-slate-400">liked</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <MessageCircle className="w-4 h-4 text-blue-400" />
                                  <span className="text-white font-medium">{engagementSummary.commented_count}</span>
                                  <span className="text-slate-400">commented</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Repeat className="w-4 h-4 text-green-400" />
                                  <span className="text-white font-medium">{engagementSummary.reposted_count}</span>
                                  <span className="text-slate-400">reposted</span>
                                </div>
                              </div>
                              {engagementSummary.engaged_users?.length > 0 && (
                                <p className="text-xs text-slate-400 mt-2">
                                  Engaged: {engagementSummary.engaged_users.join(', ')}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Modal Footer */}
                        <div className="p-4 border-t border-slate-700 bg-slate-900/50">
                          {/* View Post Button - Only if URL exists */}
                          {selectedPost.post_url ? (
                            <a
                              href={selectedPost.post_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              View Post on {selectedPost.platform}
                            </a>
                          ) : (
                            <p className="text-xs text-slate-500 text-center py-2">
                              No post URL saved. Add the URL when marking posts as published.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Activity Tab */}
                  {teamDetails && activeTab === 'activity' && (
                    <div className="bg-slate-900/50 border border-slate-700 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between p-4 border-b border-slate-700">
                        <h3 className="font-bold text-white flex items-center gap-2">
                          <Activity className="w-5 h-5 text-indigo-400" />
                          Team Activity
                        </h3>
                        <button
                          onClick={() => fetchTeamActivity(selectedTeam)}
                          className="text-sm text-slate-400 hover:text-white"
                        >
                          Refresh
                        </button>
                      </div>
                      
                      {teamActivity.length === 0 ? (
                        <div className="p-8 text-center">
                          <Activity className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                          <p className="text-slate-400">No activity yet</p>
                          <p className="text-sm text-slate-500 mt-1">Team actions will appear here</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-700 max-h-96 overflow-y-auto">
                          {teamActivity.map((activity) => (
                            <div key={activity.id} className="flex items-start gap-3 p-4 hover:bg-slate-800/30">
                              <div className="p-2 bg-slate-800 rounded-lg shrink-0">
                                {getActivityIcon(activity.action)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white">
                                  {formatActivityMessage(activity)}
                                </p>
                                {activity.details?.platforms && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    Platforms: {activity.details.platforms.join(', ')}
                                  </p>
                                )}
                                <p className="text-xs text-slate-500 mt-1">
                                  {formatTimeAgo(activity.created_at)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Team Actions */}
                  {teamDetails && (
                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                      {!isOwner && (
                        <button
                          onClick={handleLeaveTeam}
                          className="px-4 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-800/50 rounded-lg flex items-center gap-2"
                        >
                          <LogOut className="w-4 h-4" />
                          Leave Team
                        </button>
                      )}
                      {isOwner && (
                        <button
                          onClick={handleDeleteTeam}
                          className="px-4 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-800/50 rounded-lg flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Team
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamSettings;
